import { useState, useEffect, useRef, useMemo } from 'react';
import { api } from '@/lib/api';
import { motion } from 'framer-motion';
import {
  Upload, FileSpreadsheet, ArrowRight, ArrowLeft, Check, X,
  AlertTriangle, ChevronDown, ChevronRight, Save, Loader2, Eye, CheckCircle,
  Info, Plus, SkipForward, ListChecks
} from 'lucide-react';

/**
 * Unified "Upload Ingredient Master" Wizard
 *
 *   Step 1 — Upload file
 *   Step 2 — Map columns
 *   Step 3 — Preview rows
 *   Step 4 — Review changes (single unified flow):
 *              • Existing ingredients   (HIGH + MEDIUM — auto-decided, collapsed)
 *              • Possible duplicates    (LOW — decision required)
 *              • New ingredients        (NONE — default Add New, allow Skip)
 *            Commits via /api/ingredients/import-commit with a `decisions` map.
 */

const ExcelMappingWizard = ({ isOpen, onClose, onImportComplete, entityType = 'ingredient' }) => {
  // ── wizard core state ──
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // ── Step 1: upload ──
  const fileInputRef = useRef(null);
  const [fileId, setFileId] = useState(null);
  const [filename, setFilename] = useState('');
  const [headers, setHeaders] = useState([]);
  const [rowCount, setRowCount] = useState(0);
  const [sampleData, setSampleData] = useState([]);
  const [canonicalFields, setCanonicalFields] = useState([]);

  // template matching
  const [matchingTemplates, setMatchingTemplates] = useState([]);
  const [bestMatch, setBestMatch] = useState(null);
  const [showTemplatePrompt, setShowTemplatePrompt] = useState(false);
  const [appliedTemplateId, setAppliedTemplateId] = useState(null);
  const [autoApplied, setAutoApplied] = useState(false);

  // ── Step 2: mapping ──
  const [columnMapping, setColumnMapping] = useState({});
  const [suggestions, setSuggestions] = useState({});
  const [templates, setTemplates] = useState([]);
  const [selectedTemplate, setSelectedTemplate] = useState('');
  const [saveTemplate, setSaveTemplate] = useState(false);
  const [templateName, setTemplateName] = useState('');

  // ── Step 3: preview ──
  const [previewData, setPreviewData] = useState(null);

  // ── Step 4: unified review ──
  // preview returned by /api/ingredients/import-preview
  const [unifiedPreview, setUnifiedPreview] = useState(null);
  const [previewToken, setPreviewToken] = useState(null);
  //   { [row_num]: { action: 'use_existing'|'add_new'|'skip',
  //                  target_ingredient_id?, field_overrides? } }
  const [decisions, setDecisions] = useState({});
  const [importResult, setImportResult] = useState(null);

  // Load templates on mount
  useEffect(() => {
    if (isOpen) loadTemplates();
  }, [isOpen]);

  const loadTemplates = async () => {
    try {
      const response = await api.getMappingTemplates(entityType);
      setTemplates(response.data.templates || []);
    } catch (err) {
      console.error('Error loading templates:', err);
    }
  };

  const resetWizard = () => {
    setStep(1);
    setFileId(null);
    setFilename('');
    setHeaders([]);
    setRowCount(0);
    setSampleData([]);
    setColumnMapping({});
    setSuggestions({});
    setSelectedTemplate('');
    setSaveTemplate(false);
    setTemplateName('');
    setPreviewData(null);
    setUnifiedPreview(null);
    setPreviewToken(null);
    setDecisions({});
    setImportResult(null);
    setError(null);
    setMatchingTemplates([]);
    setBestMatch(null);
    setShowTemplatePrompt(false);
    setAppliedTemplateId(null);
    setAutoApplied(false);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const handleClose = () => {
    resetWizard();
    onClose();
  };

  // ── Step 1: file upload ──
  const handleFileSelect = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setLoading(true);
    setError(null);
    try {
      const formData = new FormData();
      formData.append('file', file);
      const response = await api.detectExcelColumns(formData);
      const data = response.data;
      setFileId(data.file_id);
      setFilename(data.filename);
      setHeaders(data.headers);
      setRowCount(data.row_count);
      setSampleData(data.sample_data || []);
      setSuggestions(data.suggestions || {});
      setCanonicalFields(data.canonical_fields || []);
      setMatchingTemplates(data.matching_templates || []);
      setBestMatch(data.best_match);

      const initialMapping = {};
      data.headers.forEach((header) => {
        initialMapping[header] = data.suggestions[header] || 'IGNORE';
      });
      setColumnMapping(initialMapping);

      if (data.should_auto_apply && data.best_match) {
        await autoApplyTemplate(data.best_match, data.headers, data.file_id);
      } else if (data.best_match && data.has_confident_match) {
        setShowTemplatePrompt(true);
      } else {
        setStep(2);
      }
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to process file');
    } finally {
      setLoading(false);
    }
  };

  // Auto-apply template (100% matches with preference enabled)
  const autoApplyTemplate = async (template, headerList, currentFileId) => {
    if (!template || !template.column_mapping) return;
    const newMapping = {};
    headerList.forEach((header) => { newMapping[header] = 'IGNORE'; });
    Object.entries(template.column_mapping).forEach(([excelCol, field]) => {
      if (headerList.includes(excelCol)) newMapping[excelCol] = field;
    });
    setColumnMapping(newMapping);
    setSelectedTemplate(template.template_name);
    setAppliedTemplateId(template.template_id);
    setAutoApplied(true);
    const useFileId = currentFileId || fileId;
    try {
      const previewResponse = await api.previewImport({
        file_id: useFileId,
        column_mapping: newMapping,
      });
      setPreviewData(previewResponse.data);
      setStep(3);
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to generate preview');
      setAutoApplied(false);
      setStep(2);
    }
  };

  const applyMatchedTemplate = (template) => {
    if (template && template.column_mapping) {
      const newMapping = {};
      headers.forEach((header) => { newMapping[header] = 'IGNORE'; });
      Object.entries(template.column_mapping).forEach(([excelCol, field]) => {
        if (headers.includes(excelCol)) newMapping[excelCol] = field;
      });
      setColumnMapping(newMapping);
      setSelectedTemplate(template.template_name);
      setAppliedTemplateId(template.template_id);
    }
    setShowTemplatePrompt(false);
    setStep(2);
  };

  const skipTemplateMatching = () => {
    setShowTemplatePrompt(false);
    setAppliedTemplateId(null);
    setStep(2);
  };

  const applyTemplate = (templateName) => {
    const template = templates.find((t) => t.template_name === templateName);
    if (template && template.column_mapping) {
      const newMapping = { ...columnMapping };
      headers.forEach((header) => {
        if (template.column_mapping[header]) newMapping[header] = template.column_mapping[header];
      });
      setColumnMapping(newMapping);
      setSelectedTemplate(templateName);
    }
  };

  const updateMapping = (excelColumn, canonicalField) => {
    setColumnMapping((prev) => ({ ...prev, [excelColumn]: canonicalField }));
    setSelectedTemplate('');
  };

  const isMappingValid = () => Object.values(columnMapping).includes('ingredient_name');

  // ── Step 2 → 3: generate preview ──
  const handlePreview = async () => {
    if (!isMappingValid()) {
      setError('ingredient_name must be mapped to a column');
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const response = await api.previewImport({
        file_id: fileId,
        column_mapping: columnMapping,
      });
      setPreviewData(response.data);
      setStep(3);
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to generate preview');
    } finally {
      setLoading(false);
    }
  };

  // ── Step 3 → 4: run unified preview ──
  const runUnifiedPreview = async () => {
    setLoading(true);
    setError(null);
    try {
      // Backend defaults to UPSERT — single unified flow. We deliberately do
      // NOT send an `import_mode` so the network payload carries no legacy
      // mode jargon.
      const response = await api.previewIngredientImport({
        file_id: fileId,
        column_mapping: columnMapping,
      });
      const data = response.data;
      setUnifiedPreview(data);
      setPreviewToken(data.preview_token);

      // Seed decisions from server defaults:
      //   LOW rows  → require operator (we leave empty so commit blocks)
      //   NONE rows → default to add_new (operator may flip to skip)
      //   HIGH/MEDIUM rows → auto-decided server side, but we store their
      //                       field_overrides=empty (defaults take effect).
      const seed = {};
      (data.rows || []).forEach((r) => {
        if (r.confidence === 'NONE' && r.action === 'CREATED') {
          seed[r.row_num] = { action: 'add_new', field_overrides: {} };
        }
      });
      setDecisions(seed);
      setStep(4);
    } catch (err) {
      setError(err.response?.data?.detail || 'Preview failed');
    } finally {
      setLoading(false);
    }
  };

  // ── Step 4: per-row decision updates ──
  const setDecision = (rowNum, patch) => {
    setDecisions((prev) => {
      const cur = prev[rowNum] || {};
      return { ...prev, [rowNum]: { ...cur, ...patch } };
    });
  };

  const toggleFieldOverride = (rowNum, field, checked) => {
    setDecisions((prev) => {
      const cur = prev[rowNum] || {};
      const overrides = { ...(cur.field_overrides || {}) };
      overrides[field] = !!checked;
      return { ...prev, [rowNum]: { ...cur, field_overrides: overrides } };
    });
  };

  // bulk actions for LOW
  const bulkSetLow = (action) => {
    if (!unifiedPreview) return;
    const next = { ...decisions };
    unifiedPreview.rows
      .filter((r) => r.confidence === 'LOW')
      .forEach((r) => {
        const target = r.possible_candidates?.[0]?.ingredient_id || null;
        if (action === 'use_existing') {
          next[r.row_num] = {
            action: 'use_existing',
            target_ingredient_id: target,
            field_overrides: next[r.row_num]?.field_overrides || {},
          };
        } else if (action === 'add_new') {
          next[r.row_num] = { action: 'add_new', field_overrides: {} };
        } else if (action === 'skip') {
          next[r.row_num] = { action: 'skip' };
        }
      });
    setDecisions(next);
  };

  // bulk actions for NONE
  const bulkSetNone = (action) => {
    if (!unifiedPreview) return;
    const next = { ...decisions };
    unifiedPreview.rows
      .filter((r) => r.confidence === 'NONE' && r.action === 'CREATED')
      .forEach((r) => {
        if (action === 'add_new') next[r.row_num] = { action: 'add_new', field_overrides: {} };
        else if (action === 'skip') next[r.row_num] = { action: 'skip' };
      });
    setDecisions(next);
  };

  // ── derived buckets ──
  const buckets = useMemo(() => {
    const rows = unifiedPreview?.rows || [];
    return {
      existing: rows.filter((r) => r.confidence === 'HIGH' || r.confidence === 'MEDIUM'),
      duplicates: rows.filter((r) => r.confidence === 'LOW'),
      newOnes: rows.filter((r) => r.confidence === 'NONE' && r.action === 'CREATED'),
      rejected: rows.filter((r) => r.confidence === 'REJECTED' || (r.action === 'REJECTED' && r.confidence !== 'LOW')),
    };
  }, [unifiedPreview]);

  const allLowDecided = () => {
    return buckets.duplicates.every((r) => {
      const d = decisions[r.row_num];
      if (!d || !d.action) return false;
      if (d.action === 'use_existing' && !d.target_ingredient_id) return false;
      return true;
    });
  };

  // ── Step 4: commit ──
  const handleCommit = async () => {
    if (!previewToken) {
      setError('Preview missing. Run preview again.');
      return;
    }
    if (!allLowDecided()) {
      setError('Decide every "Possible duplicate" row before confirming.');
      return;
    }
    setLoading(true);
    setError(null);
    try {
      // Send decisions keyed by row_num (string keys are fine — backend coerces).
      const payload = { preview_token: previewToken, decisions };
      const response = await api.commitIngredientImport(payload);
      const applied = response.data.applied || {};
      setError(null);
      setImportResult({
        updated: applied.updated || 0,
        created: applied.created || 0,
        skipped: applied.skipped || 0,
        rejected: applied.rejected || 0,
        noop: applied.noop || 0,
        summary: response.data.summary,
        upload_batch_id: response.data.upload_batch_id,
        audit_rows_written: response.data.audit_rows_written,
      });
      if (onImportComplete) onImportComplete(response.data);
    } catch (err) {
      const detail = err.response?.data?.detail;
      if (detail && typeof detail === 'object' && detail.error === 'DECISIONS_REQUIRED') {
        setError(detail.message || 'Some rows still need a decision.');
      } else {
        setError(typeof detail === 'string' ? detail : 'Commit failed');
      }
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  // ─────────────────────────────────────────────────────────────────────────
  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4"
      onClick={handleClose}
      data-testid="excel-mapping-wizard-overlay"
    >
      <motion.div
        initial={{ scale: 0.95, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        exit={{ scale: 0.95, opacity: 0 }}
        onClick={(e) => e.stopPropagation()}
        className="bg-white rounded-xl shadow-2xl w-full max-w-5xl max-h-[92vh] overflow-hidden flex flex-col"
        data-testid="excel-mapping-wizard"
      >
        {/* Header */}
        <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
          <div>
            <h2 className="text-xl font-bold text-gray-900">Upload Ingredient Master</h2>
            <p className="text-sm text-gray-500">
              {step === 1 && 'Step 1: Upload Excel File'}
              {step === 2 && 'Step 2: Map Columns'}
              {step === 3 && 'Step 3: Preview Data'}
              {step === 4 && (importResult ? 'Upload Complete' : 'Step 4: Review Changes')}
            </p>
          </div>
          <button
            onClick={handleClose}
            className="p-2 hover:bg-gray-100 rounded-lg"
            data-testid="close-wizard-btn"
          >
            <X size={20} />
          </button>
        </div>

        {/* Progress bar */}
        <div className="px-6 py-3 bg-gray-50 border-b">
          <div className="flex items-center gap-2">
            {[1, 2, 3, 4].map((s) => (
              <div key={s} className="flex items-center flex-1">
                <div className={`
                  w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium
                  ${step >= s ? 'bg-indigo-600 text-white' : 'bg-gray-200 text-gray-500'}
                `}>
                  {step > s ? <Check size={16} /> : s}
                </div>
                {s < 4 && (
                  <div className={`flex-1 h-1 mx-2 rounded ${step > s ? 'bg-indigo-600' : 'bg-gray-200'}`} />
                )}
              </div>
            ))}
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6">
          {error && !importResult && (
            <div
              className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg flex items-center gap-2 text-red-700"
              data-testid="wizard-error"
            >
              <AlertTriangle size={18} />
              <span className="text-sm">{error}</span>
            </div>
          )}

          {/* Step 1: Upload */}
          {step === 1 && !showTemplatePrompt && (
            <div className="space-y-6">
              <div
                className="border-2 border-dashed border-gray-300 rounded-xl p-12 text-center hover:border-indigo-400 transition-colors cursor-pointer"
                onClick={() => fileInputRef.current?.click()}
              >
                {loading ? (
                  <div className="flex flex-col items-center">
                    <Loader2 className="animate-spin text-indigo-600 mb-4" size={48} />
                    <p className="text-gray-600">Processing file...</p>
                  </div>
                ) : (
                  <>
                    <FileSpreadsheet className="mx-auto text-gray-400 mb-4" size={56} />
                    <p className="text-lg text-gray-700 mb-2">Drop your Excel file here</p>
                    <p className="text-sm text-gray-500 mb-4">or click to browse</p>
                    <button className="px-6 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700">
                      Select File
                    </button>
                    <p className="text-xs text-gray-400 mt-4">Supported: .xlsx, .xls, .csv</p>
                  </>
                )}
                <input
                  ref={fileInputRef}
                  type="file"
                  accept=".xlsx,.xls,.csv"
                  onChange={handleFileSelect}
                  className="hidden"
                  data-testid="file-input"
                />
              </div>

              <div className="bg-blue-50 rounded-lg p-4 flex items-start gap-3">
                <Info className="text-blue-600 mt-0.5" size={20} />
                <div>
                  <p className="text-sm font-medium text-blue-900">One unified upload</p>
                  <p className="text-sm text-blue-700 mt-1">
                    A single file can include new ingredients and updates to existing ones.
                    We classify each row automatically and only ask you to decide on rows that
                    look like possible duplicates.
                  </p>
                </div>
              </div>
            </div>
          )}

          {/* Template Match Prompt (after upload) */}
          {showTemplatePrompt && bestMatch && (
            <div className="space-y-6">
              <div className="bg-gray-50 rounded-lg p-4 flex items-center gap-3">
                <FileSpreadsheet className="text-green-600" size={24} />
                <div>
                  <p className="font-medium text-gray-900">{filename}</p>
                  <p className="text-sm text-gray-500">{rowCount} rows detected</p>
                </div>
              </div>

              <div className="bg-gradient-to-r from-green-50 to-emerald-50 border border-green-200 rounded-xl p-6">
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center flex-shrink-0">
                    <CheckCircle className="text-green-600" size={24} />
                  </div>
                  <div className="flex-1">
                    <h3 className="text-lg font-semibold text-gray-900 mb-1">Template Match Found!</h3>
                    <p className="text-gray-600 mb-4">We detected this file matches your saved template:</p>
                    <div className="bg-white rounded-lg border border-green-200 p-4 mb-4">
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="font-semibold text-gray-900">{bestMatch.template_name}</p>
                          <p className="text-sm text-gray-500">
                            {Math.round(bestMatch.similarity_score * 100)}% match
                            {bestMatch.usage_count > 0 && ` • Used ${bestMatch.usage_count} times`}
                          </p>
                        </div>
                        <span className={`px-3 py-1 text-sm font-medium rounded-full ${
                          bestMatch.confidence === 'high'
                            ? 'bg-green-100 text-green-800'
                            : 'bg-amber-100 text-amber-800'
                        }`}>
                          {bestMatch.confidence === 'high' ? 'High Confidence' : 'Likely Match'}
                        </span>
                      </div>
                    </div>
                    <div className="flex flex-wrap gap-3">
                      <button
                        onClick={() => applyMatchedTemplate(bestMatch)}
                        className="px-5 py-2.5 bg-green-600 text-white rounded-lg hover:bg-green-700 font-medium flex items-center gap-2"
                        data-testid="apply-template-btn"
                      >
                        <Check size={18} />
                        Apply Template
                      </button>
                      <button
                        onClick={() => applyMatchedTemplate(bestMatch)}
                        className="px-5 py-2.5 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 font-medium flex items-center gap-2"
                        data-testid="review-mapping-btn"
                      >
                        <Eye size={18} />
                        Review Mapping
                      </button>
                      <button
                        onClick={skipTemplateMatching}
                        className="px-5 py-2.5 text-gray-500 hover:text-gray-700 font-medium"
                        data-testid="ignore-template-btn"
                      >
                        Ignore & Map Manually
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Step 2: Mapping */}
          {step === 2 && (
            <div className="space-y-6">
              <div className="bg-gray-50 rounded-lg p-4 flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <FileSpreadsheet className="text-green-600" size={24} />
                  <div>
                    <p className="font-medium text-gray-900">{filename}</p>
                    <p className="text-sm text-gray-500">{rowCount} rows detected</p>
                  </div>
                </div>
                <button
                  onClick={() => { setStep(1); resetWizard(); }}
                  className="text-sm text-gray-500 hover:text-gray-700"
                >
                  Change file
                </button>
              </div>

              {templates.length > 0 && (
                <div className="flex items-center gap-3">
                  <label className="text-sm font-medium text-gray-700">Apply saved template:</label>
                  <select
                    value={selectedTemplate}
                    onChange={(e) => applyTemplate(e.target.value)}
                    className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-indigo-500"
                    data-testid="template-select"
                  >
                    <option value="">Select template...</option>
                    {templates.map((t) => (
                      <option key={t.template_name} value={t.template_name}>{t.template_name}</option>
                    ))}
                  </select>
                </div>
              )}

              <div className="border border-gray-200 rounded-lg overflow-hidden">
                <table className="w-full">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Excel Column</th>
                      <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase w-16"></th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Map To</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Sample Value</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200">
                    {headers.map((header, idx) => (
                      <tr key={header} className={columnMapping[header] === 'IGNORE' ? 'bg-gray-50 opacity-60' : ''}>
                        <td className="px-4 py-3">
                          <span className="font-medium text-gray-900">{header}</span>
                          {suggestions[header] && columnMapping[header] === suggestions[header] && (
                            <span className="ml-2 text-xs text-green-600">(auto-suggested)</span>
                          )}
                        </td>
                        <td className="px-4 py-3 text-center">
                          <ArrowRight className="text-gray-400 mx-auto" size={18} />
                        </td>
                        <td className="px-4 py-3">
                          <select
                            value={columnMapping[header] || 'IGNORE'}
                            onChange={(e) => updateMapping(header, e.target.value)}
                            className={`w-full px-3 py-2 border rounded-lg text-sm focus:ring-2 focus:ring-indigo-500 ${
                              columnMapping[header] === 'ingredient_name'
                                ? 'border-green-500 bg-green-50'
                                : columnMapping[header] === 'IGNORE'
                                ? 'border-gray-200 bg-gray-50 text-gray-400'
                                : 'border-gray-300'
                            }`}
                            data-testid={`mapping-select-${idx}`}
                          >
                            <option value="IGNORE">Ignore this column</option>
                            {canonicalFields.map((field) => (
                              <option key={field.field} value={field.field}>
                                {field.label} {field.required ? '*' : ''}
                              </option>
                            ))}
                          </select>
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-500 font-mono truncate max-w-xs">
                          {sampleData[0]?.[header] || '-'}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {!isMappingValid() && (
                <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg flex items-center gap-2 text-amber-800">
                  <AlertTriangle size={18} />
                  <span className="text-sm">
                    <strong>Required:</strong> You must map at least one column to "Ingredient Name"
                  </span>
                </div>
              )}

              <div className="flex items-center gap-4 pt-4 border-t">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={saveTemplate}
                    onChange={(e) => setSaveTemplate(e.target.checked)}
                    className="w-4 h-4 text-indigo-600 rounded"
                  />
                  <span className="text-sm text-gray-700">Save this mapping as a template</span>
                </label>
                {saveTemplate && (
                  <input
                    type="text"
                    value={templateName}
                    onChange={(e) => setTemplateName(e.target.value)}
                    placeholder="Template name..."
                    className="flex-1 px-3 py-2 border border-gray-300 rounded-lg text-sm"
                    data-testid="template-name-input"
                  />
                )}
              </div>
            </div>
          )}

          {/* Step 3: Preview */}
          {step === 3 && !importResult && (
            <div className="space-y-6">
              {autoApplied && bestMatch && (
                <div className="bg-gradient-to-r from-green-50 to-emerald-50 border border-green-200 rounded-xl p-4 flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-green-100 rounded-full flex items-center justify-center">
                      <CheckCircle className="text-green-600" size={20} />
                    </div>
                    <div>
                      <p className="font-semibold text-gray-900">
                        Template auto-applied: <span className="text-green-700">{bestMatch.template_name}</span>
                      </p>
                      <p className="text-sm text-gray-600">100% match • Skipped mapping step</p>
                    </div>
                  </div>
                  <div className="flex gap-2">
                    <button
                      onClick={() => { setAutoApplied(false); setStep(2); }}
                      className="px-3 py-1.5 text-sm border border-gray-300 text-gray-700 rounded-lg hover:bg-white flex items-center gap-1"
                    >
                      <Eye size={14} />
                      Review Mapping
                    </button>
                  </div>
                </div>
              )}

              <div className="grid grid-cols-3 gap-4">
                <SummaryStat color="gray" value={previewData?.total_rows || 0} label="Total Rows" />
                <SummaryStat color="green" value={previewData?.estimated_valid || 0} label="Valid Rows" />
                <SummaryStat color="red" value={previewData?.estimated_errors || 0} label="Invalid Rows" />
              </div>

              <div className="bg-blue-50 rounded-lg p-4">
                <p className="text-sm font-medium text-blue-900 mb-2">Mapping Summary</p>
                <div className="flex gap-4 text-sm text-blue-700">
                  <span>{previewData?.mapping_summary?.mapped_columns || 0} columns mapped</span>
                  <span>{previewData?.mapping_summary?.ignored_columns || 0} columns ignored</span>
                  {previewData?.mapping_summary?.has_price && (
                    <span className="text-green-700">Price data included</span>
                  )}
                </div>
              </div>

              <div className="border border-gray-200 rounded-lg overflow-hidden">
                <div className="bg-gray-50 px-4 py-2 border-b flex items-center gap-2">
                  <Eye size={16} className="text-gray-500" />
                  <span className="text-sm font-medium text-gray-700">Preview (first 10 rows)</span>
                </div>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-3 py-2 text-left font-medium text-gray-500">Row</th>
                        <th className="px-3 py-2 text-left font-medium text-gray-500">Ingredient Name</th>
                        <th className="px-3 py-2 text-left font-medium text-gray-500">Category</th>
                        <th className="px-3 py-2 text-left font-medium text-gray-500">Base UOM</th>
                        <th className="px-3 py-2 text-left font-medium text-gray-500">Price</th>
                        <th className="px-3 py-2 text-left font-medium text-gray-500">Status</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y">
                      {previewData?.preview_rows?.map((row, idx) => (
                        <tr key={idx} className={row._error ? 'bg-red-50' : ''}>
                          <td className="px-3 py-2 text-gray-500">{row._row}</td>
                          <td className="px-3 py-2 font-medium">{row.ingredient_name || '-'}</td>
                          <td className="px-3 py-2">{row.category || '-'}</td>
                          <td className="px-3 py-2">{row.base_uom || 'kg'}</td>
                          <td className="px-3 py-2">
                            {row.seed_price_per_base_uom ? `₹${row.seed_price_per_base_uom}` : '-'}
                          </td>
                          <td className="px-3 py-2">
                            {row._error ? (
                              <span className="text-red-600 text-xs">{row._error}</span>
                            ) : (
                              <CheckCircle size={16} className="text-green-500" />
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>

              {saveTemplate && templateName && (
                <div className="flex items-center gap-2 text-sm text-gray-600">
                  <Save size={16} />
                  <span>Template "{templateName}" will be saved for future use</span>
                </div>
              )}
            </div>
          )}

          {/* Step 4: Unified Review */}
          {step === 4 && !importResult && unifiedPreview && (
            <ReviewScreen
              preview={unifiedPreview}
              buckets={buckets}
              decisions={decisions}
              setDecision={setDecision}
              toggleFieldOverride={toggleFieldOverride}
              bulkSetLow={bulkSetLow}
              bulkSetNone={bulkSetNone}
            />
          )}

          {/* Result */}
          {step === 4 && importResult && (
            <ResultScreen result={importResult} />
          )}
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-gray-200 flex items-center justify-between bg-gray-50">
          <div>
            {step > 1 && !importResult && (
              <button
                onClick={() => setStep(step - 1)}
                className="px-4 py-2 text-gray-600 hover:text-gray-800 flex items-center gap-2"
                disabled={loading}
                data-testid="back-btn"
              >
                <ArrowLeft size={18} />
                Back
              </button>
            )}
          </div>

          <div className="flex gap-3">
            <button
              onClick={handleClose}
              className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50"
              data-testid="cancel-btn"
            >
              {importResult ? 'Close' : 'Cancel'}
            </button>

            {step === 2 && (
              <button
                onClick={handlePreview}
                disabled={!isMappingValid() || loading}
                className="px-6 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                data-testid="preview-btn"
              >
                {loading ? (<><Loader2 size={18} className="animate-spin" /> Processing...</>)
                         : (<>Preview <ArrowRight size={18} /></>)}
              </button>
            )}

            {step === 3 && !importResult && (
              <button
                onClick={runUnifiedPreview}
                disabled={loading}
                className="px-6 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 disabled:opacity-50 flex items-center gap-2"
                data-testid="run-review-btn"
              >
                {loading ? (<><Loader2 size={18} className="animate-spin" /> Building review...</>)
                         : (<>Review Changes <ArrowRight size={18} /></>)}
              </button>
            )}

            {step === 4 && !importResult && (
              <button
                onClick={handleCommit}
                disabled={loading || !allLowDecided()}
                className="px-6 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                data-testid="confirm-upload-btn"
                title={!allLowDecided() ? 'Decide every "Possible duplicate" row first' : ''}
              >
                {loading ? (<><Loader2 size={18} className="animate-spin" /> Uploading...</>)
                         : (<><Check size={18} /> Confirm Upload</>)}
              </button>
            )}
          </div>
        </div>
      </motion.div>
    </motion.div>
  );
};

// ─── helpers ────────────────────────────────────────────────────────────────

const SummaryStat = ({ color, value, label }) => {
  const palette = {
    gray: 'bg-gray-50 text-gray-900',
    green: 'bg-green-50 text-green-600',
    red: 'bg-red-50 text-red-600',
    indigo: 'bg-indigo-50 text-indigo-600',
    amber: 'bg-amber-50 text-amber-700',
  }[color] || 'bg-gray-50 text-gray-900';
  return (
    <div className={`${palette} rounded-lg p-4 text-center`}>
      <p className="text-3xl font-bold">{value}</p>
      <p className="text-sm text-gray-500">{label}</p>
    </div>
  );
};

// ── Step 4: Unified Review Screen ───────────────────────────────────────────

const ReviewScreen = ({
  preview, buckets, decisions, setDecision, toggleFieldOverride,
  bulkSetLow, bulkSetNone,
}) => {
  const total = preview.summary?.total ?? preview.rows?.length ?? 0;
  const rejected = buckets.rejected || [];
  const signals = preview.summary?.unsafe_signals || [];

  return (
    <div className="space-y-5" data-testid="unified-review">
      {/* Unsafe signals banner (non-blocking) */}
      {signals.length > 0 && (
        <div
          className="bg-amber-50 border border-amber-300 rounded-xl p-4"
          data-testid="unsafe-banner"
        >
          <div className="flex items-start gap-3">
            <AlertTriangle className="text-amber-700 mt-0.5 flex-shrink-0" size={20} />
            <div>
              <p className="text-sm font-semibold text-amber-900 mb-2">
                Heads up — review these before confirming
              </p>
              <ul className="space-y-1">
                {signals.map((s) => (
                  <li key={s.code} className="text-sm text-amber-800" data-testid={`unsafe-${s.code}`}>
                    • {s.message}
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      )}

      {/* High-level summary */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="review-summary">
        <SummaryStat color="green"  value={buckets.existing.length}   label="Existing ingredients" />
        <SummaryStat color="amber"  value={buckets.duplicates.length} label="Possible duplicates" />
        <SummaryStat color="indigo" value={buckets.newOnes.length}    label="New ingredients" />
        <SummaryStat color="red"    value={rejected.length}           label="Rejected" />
      </div>

      {/* 1. Possible duplicates (require decision) — shown FIRST */}
      {buckets.duplicates.length > 0 && (
        <DuplicatesBucket
          rows={buckets.duplicates}
          decisions={decisions}
          setDecision={setDecision}
          toggleFieldOverride={toggleFieldOverride}
          bulkSetLow={bulkSetLow}
        />
      )}

      {/* 2. New ingredients (default add_new, allow skip) */}
      {buckets.newOnes.length > 0 && (
        <NewBucket
          rows={buckets.newOnes}
          decisions={decisions}
          setDecision={setDecision}
          bulkSetNone={bulkSetNone}
        />
      )}

      {/* 3. Existing ingredients (collapsed by default) */}
      {buckets.existing.length > 0 && (
        <ExistingBucket rows={buckets.existing} />
      )}

      {/* 4. Rejected (collapsed) */}
      {rejected.length > 0 && (
        <RejectedBucket rows={rejected} />
      )}

      {total === 0 && (
        <div className="text-center py-8 text-gray-500">No rows to review.</div>
      )}
    </div>
  );
};

// ── Bucket: Possible duplicates (LOW) ───────────────────────────────────────

const DuplicatesBucket = ({ rows, decisions, setDecision, toggleFieldOverride, bulkSetLow }) => {
  return (
    <div className="border-2 border-amber-300 rounded-xl overflow-hidden" data-testid="bucket-duplicates">
      <div className="bg-amber-50 px-4 py-3 border-b border-amber-200 flex items-center justify-between flex-wrap gap-2">
        <div className="flex items-center gap-2">
          <AlertTriangle className="text-amber-700" size={18} />
          <p className="text-sm font-semibold text-amber-900">
            Possible duplicates — {rows.length} row{rows.length === 1 ? '' : 's'} need your decision
          </p>
        </div>
        <div className="flex gap-2">
          <button
            onClick={() => bulkSetLow('use_existing')}
            className="px-3 py-1.5 text-xs bg-white border border-amber-300 text-amber-800 rounded-lg hover:bg-amber-100"
            data-testid="bulk-low-use-existing"
          >
            All → Use Existing
          </button>
          <button
            onClick={() => bulkSetLow('add_new')}
            className="px-3 py-1.5 text-xs bg-white border border-amber-300 text-amber-800 rounded-lg hover:bg-amber-100"
            data-testid="bulk-low-add-new"
          >
            All → Add New
          </button>
          <button
            onClick={() => bulkSetLow('skip')}
            className="px-3 py-1.5 text-xs bg-white border border-amber-300 text-amber-800 rounded-lg hover:bg-amber-100"
            data-testid="bulk-low-skip"
          >
            All → Skip
          </button>
        </div>
      </div>
      <div className="divide-y divide-amber-100 max-h-[480px] overflow-y-auto">
        {rows.map((r) => (
          <DuplicateRow
            key={r.row_num}
            row={r}
            decision={decisions[r.row_num]}
            setDecision={setDecision}
            toggleFieldOverride={toggleFieldOverride}
          />
        ))}
      </div>
    </div>
  );
};

const DuplicateRow = ({ row, decision, setDecision, toggleFieldOverride }) => {
  const action = decision?.action || '';
  const target = decision?.target_ingredient_id
    || row.possible_candidates?.[0]?.ingredient_id
    || '';
  const decided = !!action && !(action === 'use_existing' && !target);

  return (
    <div className="px-4 py-3" data-testid={`duplicate-row-${row.row_num}`}>
      <div className="flex items-center justify-between mb-2 gap-3 flex-wrap">
        <div className="flex items-center gap-2 min-w-0">
          <span className="text-xs text-gray-400 font-mono">#{row.row_num}</span>
          <span className="font-semibold text-gray-900 truncate">
            {row.ingredient_name || row.ingredient_name_attempted || '—'}
          </span>
        </div>
        <span
          className={`px-2 py-0.5 rounded text-[10px] font-bold ${
            decided ? 'bg-green-100 text-green-800' : 'bg-amber-100 text-amber-800'
          }`}
          data-testid={`duplicate-status-${row.row_num}`}
        >
          {decided ? 'DECIDED' : 'NEEDS DECISION'}
        </span>
      </div>

      {/* Candidates */}
      {row.possible_candidates && row.possible_candidates.length > 0 && (
        <div className="space-y-1 mb-3">
          <p className="text-xs text-gray-500">Matches an existing ingredient:</p>
          {row.possible_candidates.map((c) => (
            <label
              key={c.ingredient_id}
              className={`flex items-center justify-between gap-2 px-2 py-1.5 rounded cursor-pointer text-sm ${
                target === c.ingredient_id
                  ? 'bg-indigo-50 border border-indigo-300'
                  : 'hover:bg-gray-50 border border-transparent'
              }`}
            >
              <div className="flex items-center gap-2 min-w-0">
                <input
                  type="radio"
                  name={`target-${row.row_num}`}
                  checked={target === c.ingredient_id}
                  onChange={() => setDecision(row.row_num, {
                    action: action || 'use_existing',
                    target_ingredient_id: c.ingredient_id,
                  })}
                  className="w-3 h-3"
                  data-testid={`candidate-${row.row_num}-${c.ingredient_id}`}
                />
                <span className="font-medium text-gray-800 truncate">{c.ingredient_name}</span>
                {c.ingredient_category && (
                  <span className="text-[10px] text-gray-500">· {c.ingredient_category}</span>
                )}
                {c.ingredient_base_unit && (
                  <span className="text-[10px] text-gray-500">· {c.ingredient_base_unit}</span>
                )}
              </div>
              <div className="flex items-center gap-2 flex-shrink-0">
                <span className="text-[10px] font-mono text-gray-600">
                  {Math.round((c.confidence_score || 0) * 100)}% match
                </span>
              </div>
            </label>
          ))}
        </div>
      )}

      {/* Action buttons */}
      <div className="flex flex-wrap gap-2 mb-2">
        <ActionButton
          icon={<Check size={12} />}
          label="Use Existing"
          active={action === 'use_existing'}
          onClick={() => setDecision(row.row_num, {
            action: 'use_existing',
            target_ingredient_id: target || row.possible_candidates?.[0]?.ingredient_id || null,
          })}
          testId={`action-${row.row_num}-use-existing`}
        />
        <ActionButton
          icon={<Plus size={12} />}
          label="Add New"
          active={action === 'add_new'}
          onClick={() => setDecision(row.row_num, {
            action: 'add_new',
            target_ingredient_id: null,
            field_overrides: {},
          })}
          testId={`action-${row.row_num}-add-new`}
        />
        <ActionButton
          icon={<SkipForward size={12} />}
          label="Skip"
          active={action === 'skip'}
          onClick={() => setDecision(row.row_num, { action: 'skip', target_ingredient_id: null })}
          testId={`action-${row.row_num}-skip`}
        />
      </div>

      {/* Inline diff — shown only when "Use Existing" is selected */}
      {action === 'use_existing' && target && (
        <InlineDiff
          row={row}
          target={row.possible_candidates?.find((c) => c.ingredient_id === target) || row.possible_candidates?.[0]}
          overrides={decision?.field_overrides || {}}
          defaults={row.field_update_defaults || {}}
          onToggle={(field, checked) => toggleFieldOverride(row.row_num, field, checked)}
        />
      )}
    </div>
  );
};

const FIELD_LABELS = {
  seed_price_per_base_uom: 'Price',
  ingredient_category: 'Category',
  subcategory: 'Subcategory',
  sku_size: 'SKU size',
  pack_size: 'Pack size',
  tax_percent: 'Tax %',
  vendor_name: 'Vendor',
};

const InlineDiff = ({ row, target, overrides, defaults, onToggle }) => {
  // Build the set of fields with values in the uploaded row that differ from
  // the target candidate. The backend already gave us field_update_defaults,
  // so we only show fields that appear in that map (toggleable) PLUS any
  // explicit override the operator already flipped on.
  const newValues = row.new_values || {};
  // For LOW rows the preview row's new_values can be empty (since the row
  // resolved as CREATED). Fall back to row.previous_values absent → just
  // iterate over field_update_defaults keys which the backend computed using
  // the parsed patch on the LOW candidate.
  // Only fields the operator should ever see/toggle. Filters out advisory
  // fields the backend might (now or in the future) include in defaults.
  const ALLOWED_DIFF_FIELDS = Object.keys(FIELD_LABELS);
  const fields = Object.keys(defaults).filter((f) => ALLOWED_DIFF_FIELDS.includes(f));

  if (fields.length === 0) {
    return (
      <div className="mt-2 px-3 py-2 bg-gray-50 rounded text-xs text-gray-500">
        No field-level changes to apply on this match.
      </div>
    );
  }

  const valueOf = (obj, field) => {
    if (!obj) return undefined;
    const v = obj[field];
    if (v === undefined || v === null || v === '') return '—';
    return String(v);
  };

  return (
    <div
      className="mt-2 border border-gray-200 rounded-lg bg-gray-50/60"
      data-testid={`diff-${row.row_num}`}
    >
      <div className="px-3 py-2 border-b border-gray-200 flex items-center gap-2">
        <ListChecks size={14} className="text-gray-500" />
        <span className="text-xs font-medium text-gray-700">
          Choose which fields to update on the existing ingredient
        </span>
      </div>
      <div className="divide-y divide-gray-200">
        {fields.map((field) => {
          const checked = overrides[field] !== undefined ? overrides[field] : !!defaults[field];
          const newVal = valueOf(newValues, field);
          const existingVal = valueOf(target, field);
          return (
            <label
              key={field}
              className="flex items-center gap-3 px-3 py-2 cursor-pointer hover:bg-white"
              data-testid={`diff-toggle-${row.row_num}-${field}`}
            >
              <input
                type="checkbox"
                checked={checked}
                onChange={(e) => onToggle(field, e.target.checked)}
                className="w-4 h-4 text-indigo-600 rounded"
              />
              <span className="text-xs font-medium text-gray-700 w-28 flex-shrink-0">
                {FIELD_LABELS[field] || field}
              </span>
              <span className="text-xs font-mono text-red-600 line-through truncate" title={existingVal}>
                {existingVal}
              </span>
              <ArrowRight size={12} className="text-gray-400 flex-shrink-0" />
              <span className="text-xs font-mono text-green-700 truncate" title={newVal}>
                {newVal}
              </span>
            </label>
          );
        })}
      </div>
    </div>
  );
};

// ── Bucket: New ingredients (NONE) ──────────────────────────────────────────

const NewBucket = ({ rows, decisions, setDecision, bulkSetNone }) => {
  return (
    <div className="border border-indigo-200 rounded-xl overflow-hidden" data-testid="bucket-new">
      <div className="bg-indigo-50 px-4 py-3 border-b border-indigo-200 flex items-center justify-between flex-wrap gap-2">
        <div className="flex items-center gap-2">
          <Plus className="text-indigo-700" size={18} />
          <p className="text-sm font-semibold text-indigo-900">
            New ingredients — {rows.length} row{rows.length === 1 ? '' : 's'} will be added
          </p>
        </div>
        <div className="flex gap-2">
          <button
            onClick={() => bulkSetNone('add_new')}
            className="px-3 py-1.5 text-xs bg-white border border-indigo-300 text-indigo-700 rounded-lg hover:bg-indigo-100"
            data-testid="bulk-new-add"
          >
            All → Add New
          </button>
          <button
            onClick={() => bulkSetNone('skip')}
            className="px-3 py-1.5 text-xs bg-white border border-indigo-300 text-indigo-700 rounded-lg hover:bg-indigo-100"
            data-testid="bulk-new-skip"
          >
            All → Skip
          </button>
        </div>
      </div>
      <div className="divide-y divide-indigo-100 max-h-80 overflow-y-auto">
        {rows.map((r) => {
          const action = decisions[r.row_num]?.action || 'add_new';
          const nv = r.new_values || {};
          return (
            <div
              key={r.row_num}
              className="px-4 py-2 flex items-center justify-between gap-3 text-sm"
              data-testid={`new-row-${r.row_num}`}
            >
              <div className="flex items-center gap-3 min-w-0">
                <span className="text-xs text-gray-400 font-mono">#{r.row_num}</span>
                <span className="font-medium text-gray-900 truncate">{r.ingredient_name || '—'}</span>
                <span className="text-xs text-gray-500 truncate">
                  {[nv.ingredient_category, nv.ingredient_base_unit,
                    nv.seed_price_per_base_uom != null ? `₹${nv.seed_price_per_base_uom}` : null]
                    .filter(Boolean).join(' · ')}
                </span>
              </div>
              <div className="flex gap-2 flex-shrink-0">
                <ActionButton
                  icon={<Plus size={12} />}
                  label="Add New"
                  active={action === 'add_new'}
                  onClick={() => setDecision(r.row_num, { action: 'add_new', field_overrides: {} })}
                  testId={`new-${r.row_num}-add`}
                />
                <ActionButton
                  icon={<SkipForward size={12} />}
                  label="Skip"
                  active={action === 'skip'}
                  onClick={() => setDecision(r.row_num, { action: 'skip' })}
                  testId={`new-${r.row_num}-skip`}
                />
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

// ── Bucket: Existing ingredients (HIGH + MEDIUM) — collapsed by default ─────

const ExistingBucket = ({ rows }) => {
  const [open, setOpen] = useState(false);
  const updated = rows.filter((r) => r.fields_updated?.length);
  const unchanged = rows.length - updated.length;

  return (
    <div className="border border-green-200 rounded-xl overflow-hidden" data-testid="bucket-existing">
      <button
        onClick={() => setOpen((v) => !v)}
        className="w-full bg-green-50 px-4 py-3 border-b border-green-200 flex items-center justify-between hover:bg-green-100"
        data-testid="bucket-existing-toggle"
      >
        <div className="flex items-center gap-2">
          {open ? <ChevronDown size={16} className="text-green-700" /> : <ChevronRight size={16} className="text-green-700" />}
          <CheckCircle className="text-green-700" size={18} />
          <p className="text-sm font-semibold text-green-900">
            Existing ingredients — {rows.length} row{rows.length === 1 ? '' : 's'}
          </p>
        </div>
        <span className="text-xs text-green-800">
          {updated.length} will be updated · {unchanged} unchanged
        </span>
      </button>
      {open && (
        <div className="max-h-80 overflow-y-auto divide-y divide-green-100">
          {rows.map((r) => (
            <div
              key={r.row_num}
              className="px-4 py-2 flex items-center justify-between gap-3 text-sm"
              data-testid={`existing-row-${r.row_num}`}
            >
              <div className="flex items-center gap-2 min-w-0">
                <span className="text-xs text-gray-400 font-mono">#{r.row_num}</span>
                <span className="font-medium text-gray-900 truncate">
                  {r.ingredient_name || '—'}
                </span>
              </div>
              <div className="flex items-center gap-3 flex-shrink-0">
                {r.fields_updated && r.fields_updated.length > 0 ? (
                  <div className="flex flex-wrap gap-1">
                    {r.fields_updated.slice(0, 4).map((f) => (
                      <span
                        key={f}
                        className="px-1.5 py-0.5 text-[10px] bg-green-100 text-green-800 rounded font-medium"
                      >
                        {FIELD_LABELS[f] || f}
                      </span>
                    ))}
                    {r.fields_updated.length > 4 && (
                      <span className="text-[10px] text-green-700">+{r.fields_updated.length - 4}</span>
                    )}
                  </div>
                ) : (
                  <span className="text-[10px] text-gray-500 uppercase">No changes</span>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

// ── Bucket: Rejected (collapsed) ────────────────────────────────────────────

const REJECT_REASON_LABELS = {
  ingredient_id_not_found: 'ID not found in this org',
  ambiguous_name_match: 'Multiple ingredients share this name',
  ingredient_name_missing_or_too_short: 'Ingredient name missing or too short',
};

const RejectedBucket = ({ rows }) => {
  const [open, setOpen] = useState(false);
  return (
    <div className="border border-red-200 rounded-xl overflow-hidden" data-testid="bucket-rejected">
      <button
        onClick={() => setOpen((v) => !v)}
        className="w-full bg-red-50 px-4 py-3 border-b border-red-200 flex items-center justify-between hover:bg-red-100"
        data-testid="bucket-rejected-toggle"
      >
        <div className="flex items-center gap-2">
          {open ? <ChevronDown size={16} className="text-red-700" /> : <ChevronRight size={16} className="text-red-700" />}
          <X className="text-red-700" size={18} />
          <p className="text-sm font-semibold text-red-900">
            Rejected — {rows.length} row{rows.length === 1 ? '' : 's'} cannot be applied
          </p>
        </div>
      </button>
      {open && (
        <div className="max-h-72 overflow-y-auto divide-y divide-red-100">
          {rows.map((r) => (
            <div key={r.row_num} className="px-4 py-2 text-sm" data-testid={`rejected-row-${r.row_num}`}>
              <div className="flex items-center gap-2">
                <span className="text-xs text-gray-400 font-mono">#{r.row_num}</span>
                <span className="font-medium text-gray-900 truncate">
                  {r.ingredient_name || r.ingredient_name_attempted || '—'}
                </span>
              </div>
              <p className="text-xs text-red-700 mt-0.5">
                {REJECT_REASON_LABELS[r.reason] || r.reason || 'Cannot be processed'}
              </p>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

// ── Result Screen ───────────────────────────────────────────────────────────

const ResultScreen = ({ result }) => (
  <div className="space-y-6">
    <div className="text-center py-8">
      <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
        <CheckCircle className="text-green-600" size={40} />
      </div>
      <h3 className="text-2xl font-bold text-gray-900 mb-2">Upload Complete</h3>
      <p className="text-gray-600">
        {result.created} added · {result.updated} updated · {result.skipped} skipped · {result.rejected} rejected
      </p>
    </div>

    <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
      <div className="bg-indigo-50 rounded-lg p-4 text-center" data-testid="result-created">
        <p className="text-3xl font-bold text-indigo-600">{result.created || 0}</p>
        <p className="text-xs text-gray-500 mt-1">Added</p>
      </div>
      <div className="bg-green-50 rounded-lg p-4 text-center" data-testid="result-updated">
        <p className="text-3xl font-bold text-green-600">{result.updated || 0}</p>
        <p className="text-xs text-gray-500 mt-1">Updated</p>
      </div>
      <div className="bg-gray-100 rounded-lg p-4 text-center" data-testid="result-noop">
        <p className="text-3xl font-bold text-gray-700">{result.noop || 0}</p>
        <p className="text-xs text-gray-500 mt-1">Unchanged</p>
      </div>
      <div className="bg-amber-50 rounded-lg p-4 text-center" data-testid="result-skipped">
        <p className="text-3xl font-bold text-amber-700">{result.skipped || 0}</p>
        <p className="text-xs text-gray-500 mt-1">Skipped</p>
      </div>
      <div className="bg-red-50 rounded-lg p-4 text-center" data-testid="result-rejected">
        <p className="text-3xl font-bold text-red-600">{result.rejected || 0}</p>
        <p className="text-xs text-gray-500 mt-1">Rejected</p>
      </div>
    </div>

    {result.audit_rows_written !== undefined && (
      <div className="p-3 bg-blue-50 border border-blue-200 rounded-lg flex items-center gap-2 text-blue-700">
        <Save size={18} />
        <span className="text-sm">
          {result.audit_rows_written} audit row{result.audit_rows_written === 1 ? '' : 's'} recorded
          {result.upload_batch_id ? ` · batch ${result.upload_batch_id.slice(0, 8)}…` : ''}
        </span>
      </div>
    )}
  </div>
);

const ActionButton = ({ icon, label, active, onClick, testId }) => (
  <button
    onClick={onClick}
    data-testid={testId}
    className={`px-3 py-1 text-xs rounded-md border transition-colors flex items-center gap-1 ${
      active
        ? 'bg-indigo-600 text-white border-indigo-600'
        : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
    }`}
  >
    {icon}
    {label}
  </button>
);

export default ExcelMappingWizard;
